import { CIActionBtnGroup, CIStatusCapsule } from 'ccs-ui-components';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import AssociationList from '../../association-list/AssociationList';

import useJourneyEventAssociation from '../../../../../hooks/events/association-list/useJourneyEventAssociation';

import { journeyBuilderRoutes } from '../../../../../../journey-builder/constants/JdRoutes';
import { associationEventType } from '../../../../../constants';
import { IService } from '../../../../../models';
import classes from './JourneyAssociationList.module.scss';
import { IJourneyData } from '../../../../../store/actions/types/sources/GetAllJourneys';

interface JourneyState {
  totalJourneysCount?: number ;
  journeyList?: IJourneyData[];
}

interface IJourneyList {
  selectedService: IService,
  queryParams: { [key: string]: string }
}
const CIGridLabels = {
  JOURNEY_NAME: 'journeyName',
  JOURNEY_STATUS: 'status',
};

const JourneyAssociationList = ({ selectedService, queryParams }: IJourneyList) => {
  const [journeyData, setJourneyData] = useState<JourneyState | undefined>();

  const { t: translate } = useTranslation();

  const { journeyEventAssociation, isLoading } = useJourneyEventAssociation();

  const navigate = useNavigate();

  const viewJourney = (journeyName: string, JourneyVersion: string) => {
    navigate(`${journeyBuilderRoutes.journeyCanvas.replace(':id', journeyName ?? '').replace(':mode', 'view').replace(':version', JourneyVersion ?? '')}`);
  };

  const serviceType = useMemo(() => queryParams?.eventType ?? associationEventType.INGRESS, [queryParams]);

  /**
   * Renders a button group for a row in the service list.
   * @param row - The row object.
   * @returns The rendered button group.
   */
  const renderButtonGrp = (row: IJourneyData) => <CIActionBtnGroup buttons={[{
    label: translate('info_message.lbl_journey_navigation'),
    key: 'viewIcon',
    onClick: () => { viewJourney(row.journeyName, row.version.toString()); },
    testId: 'journey-nav-button'
  }]}
  />;

  /**
  * Renders the status capsule component based on the provided row status.
  * @param row - The row object containing the status property.
  * @returns The CIStatusCapsule component if the row status is truthy, otherwise null.
  */
  const renderStatus = (row: { status: string }) => (
    row.status && <CIStatusCapsule status={row.status} />
  );

  /**
  * @method renderJourneyNameAndVersion
  * @description render journey name and version
  */
  const renderJourneyNameAndVersion = (row: { journeyName: string; version: string }) => (
    <div className={classes['journey-name-wrapper']}>
      <p className={classes['journey-name']} title={row.journeyName}>{row.journeyName}</p>
      {row.version && <p className={classes['journey-version']} title={`Ver ${row.version}`}>{`Ver ${row.version}`}</p>}
    </div>
  );

  const totalJourneyCountLable = journeyData?.totalJourneysCount ? `(${journeyData?.totalJourneysCount})` : '';

  const headers = [
    {
      label: `${translate('journey_list_component.lbl_journey_name')} ${totalJourneyCountLable}`,
      name: CIGridLabels.JOURNEY_NAME,
      options: { breakPointWidth: 6 },
      columnData: { field: '', render: renderJourneyNameAndVersion },
    },
    {
      label: translate('journey_list_component.lbl_status'),
      name: CIGridLabels.JOURNEY_STATUS,
      options: { breakPointWidth: 5 },
      columnData: { field: '', render: renderStatus },
    },
    {
      name: 'journey-redirection',
      options: { breakPointWidth: 1 },
      columnData: { field: '', render: renderButtonGrp },
    }
  ];

  useEffect(() => {
    if (!isEmpty(selectedService) && selectedService?.serviceName) {
      const { serviceName, version: serviceVersion } = selectedService;
      journeyEventAssociation(serviceName, `${serviceVersion}`)?.then(async ({ journeyAssociationRecords }) => {
        setJourneyData({
          totalJourneysCount: journeyAssociationRecords?.totalJourneysCount,
          journeyList: journeyAssociationRecords?.journeyList
        });
      });
    }
  }, [selectedService]);

  useEffect(() => {
    setJourneyData({});
  }, [serviceType]);

  return (
    <div className={classes['journey-list-container']}>
      {
        !isEmpty(selectedService) && selectedService.serviceName && !isEmpty(journeyData) && (
          <>
            <div className={classes['journey-list-header-wrapper']}>
              <div
                className={classes['journey-list-header-title']}
                title={`${selectedService.serviceName} ${translate('event_associations.lbl_association_journey')}`}
              >
                <span className={classes['journey-list-header-name']}>
                  {selectedService.serviceName}
                </span>
                <span className={classes['journey-list-header-suffix']}>
                  {translate('event_associations.lbl_association_journey')}
                </span>
              </div>
              {selectedService.version && (
                <p
                  className={classes['journey-list-header-version']}
                  title={translate('event_list_component.txt_version', { replace: { version: selectedService.version } })}
                >
                  {translate('event_list_component.txt_version', { replace: { version: selectedService.version } })}
                </p>
              )}
            </div>
            <AssociationList
              headers={headers}
              tableData={journeyData?.journeyList}
              classNameBody="journey-association-table-body"
              isLoading={isLoading}
            />
          </>
        )
      }
      {
        (!selectedService.serviceName || isEmpty(journeyData)) && (
          <div className={classes['journey-list-empty']} id="journey-list-empty">
            <p>{`${translate('event_associations.desc_no_service_selected')}`}</p>
          </div>
        )
      }
    </div>
  );
};

export default JourneyAssociationList;
